import * as S from "sanctuary";
import { createSelector } from "reselect";
import * as Nested from "@/util/Nested";

import {
  getCurrentUserAgencies,
  getCurrentUserId,
  getCurrentUserRole,
  getCurrentUserPermissions,
} from "@/components/currentUser/CurrentUser.state";
import {
  SUBMIT_ACTIVATE_ADMINISTERED_USER,
  SUBMIT_DEACTIVATE_ADMINISTERED_USER,
  SUBMIT_REACTIVATE_ADMINISTERED_USER,
  getUserBeingEdited,
  openEditUserForm,
  openPersonalInfoForm,
} from "@/components/userSettingsPage/UserSettingsPage.state";
import { getPointOfSaleEnabled } from "../../state/client.state";
import { transformRoleCombinations } from "@/util/common";
import { OKTA_STATUSES } from "../../sanctuary/typedefs";
import {
  CB_ADMIN,
  CITYBASE_ADMIN,
  CLIENT_ADMIN,
  OKTA_CLIENT_ADMIN,
  SELECT_ALL_SLUG,
} from "../../sanctuary/typedefs";
import { roleDictionary } from "../../sanctuary/typedefs";
import { AgencyIcon, PersonIcon, PaymentStatusIcon } from "@thecb/components";

export const RESULTS_PER_PAGE = 20;

export const selectAdministeredUsersCache = createSelector(
  (state) => state.administeredUsers.usersCache.value,
  (usersCacheValue) => usersCacheValue
);

export const selectAppliedFilters = createSelector(
  (state) => state.administeredUsers.appliedFilters,
  (appliedFiltersValue) => appliedFiltersValue
);

export const selectAllFilterOptions = createSelector(
  [selectAdministeredUsersCache],
  (users) => getAllFilterOptions(users)
);

export const REQUEST_ADMINISTERED_USERS =
  "administeredUsers/REQUEST_ADMINISTERED_USERS";
export const requestAdministeredUsers = () => ({
  type: REQUEST_ADMINISTERED_USERS,
});

export const GET_ADMINISTERED_USERS_SUCCEEDED =
  "administeredUsers/GET_ADMINISTERED_USERS_SUCCEEDED";
export const getAdministeredUsersSucceeded = ({ users }) => ({
  type: GET_ADMINISTERED_USERS_SUCCEEDED,
  payload: {
    users,
  },
});

export const GET_ADMINISTERED_USERS_FAILED =
  "administeredUsers/GET_ADMINISTERED_USERS_FAILED";
export const getAdministeredUsersFailed = ({ error }) => ({
  type: GET_ADMINISTERED_USERS_FAILED,
  error: true,
  payload: error,
});

export const FETCH_ROLE_COMBINATIONS = "administeredUsers/FETCH_ROLE_COMBINATIONS";
export const fetchRoleCombinations = () => ({
  type: FETCH_ROLE_COMBINATIONS,
});

export const FETCH_ROLE_COMBINATIONS_SUCCEEDED =
  "administeredUsers/FETCH_ROLE_COMBINATIONS_SUCCEEDED";
export const fetchRoleCombinationsSucceeded = ({ roleCombinations }) => ({
  type: FETCH_ROLE_COMBINATIONS_SUCCEEDED,
  payload: {
    roleCombinations,
  },
});

export const FETCH_ROLE_COMBINATIONS_FAILED =
  "administeredUsers/FETCH_ROLE_COMBINATIONS_FAILED";
export const fetchRoleCombinationsFailed = (error) => ({
  type: FETCH_ROLE_COMBINATIONS_FAILED,
  payload: error,
});

export const CREATE_ADMINISTERED_USER = "administeredUsers/CREATE_ADMINISTERED_USER";
export const createAdministeredUser = ({
  role,
  pos_role,
  email,
  client,
  agencies,
  firstName,
  lastName,
  formType,
}) => ({
  type: CREATE_ADMINISTERED_USER,
  payload: {
    role,
    pos_role,
    email,
    client,
    agencies,
    firstName,
    lastName,
    formType,
  },
});

export const CREATE_ADMINISTERED_USER_SUCCEEDED =
  "administeredUsers/CREATE_ADMINISTERED_USER_SUCCEEDED";
export const createAdministeredUserSucceeded = ({ user }) => ({
  type: CREATE_ADMINISTERED_USER_SUCCEEDED,
  payload: {
    user,
  },
});

export const CREATE_ADMINISTERED_USER_FAILED =
  "administeredUsers/CREATE_ADMINISTERED_USER_FAILED";
export const createAdministeredUserFailed = ({ error }) => ({
  type: CREATE_ADMINISTERED_USER_FAILED,
  error: true,
  payload: error,
});

export const CLEAR_CREATE_ADMINISTERED_USER_REQUEST_STATE =
  "administeredUsers/CLEAR_CREATE_ADMINISTERED_USER_REQUEST_STATE";
export const clearCreateAdministeredUserRequestState = () => ({
  type: CLEAR_CREATE_ADMINISTERED_USER_REQUEST_STATE,
});

export const EDIT_ADMINISTERED_USER = "administeredUsers/EDIT_ADMINISTERED_USER";
export const editAdministeredUser = ({
  role,
  pos_role,
  client,
  agencies,
  firstName,
  lastName,
  timezone,
  userId,
}) => ({
  type: EDIT_ADMINISTERED_USER,
  payload: {
    role,
    pos_role,
    client,
    agencies,
    firstName,
    lastName,
    timezone,
    userId,
  },
});

export const EDIT_ADMINISTERED_USER_SUCCEEDED =
  "administeredUsers/EDIT_ADMINISTERED_USER_SUCCEEDED";
export const editAdministeredUserSucceeded = ({ user, userId }) => ({
  type: EDIT_ADMINISTERED_USER_SUCCEEDED,
  payload: {
    userId,
    user,
  },
});

export const EDIT_ADMINISTERED_USER_FAILED =
  "administeredUsers/EDIT_ADMINISTERED_USER_FAILED";
export const editAdministeredUserFailed = ({ error, userId }) => ({
  type: EDIT_ADMINISTERED_USER_FAILED,
  error: true,
  payload: {
    ...error,
    userId,
  },
});

export const ACTIVATE_ADMINISTERED_USER =
  "administeredUsers/ACTIVATE_ADMINISTERED_USER";
export const activateAdministeredUser = ({ userId }) => ({
  type: ACTIVATE_ADMINISTERED_USER,
  payload: {
    userId,
  },
});

export const ACTIVATE_ADMINISTERED_USER_SUCCEEDED =
  "administeredUsers/ACTIVATE_ADMINISTERED_USER_SUCCEEDED";
export const activateAdministeredUserSucceeded = ({ user, userId }) => ({
  type: ACTIVATE_ADMINISTERED_USER_SUCCEEDED,
  payload: {
    user,
    userId,
  },
});

export const ACTIVATE_ADMINISTERED_USER_FAILED =
  "administeredUsers/ACTIVATE_ADMINISTERED_USER_FAILED";
export const activateAdministeredUserFailed = ({ error, userId }) => ({
  type: ACTIVATE_ADMINISTERED_USER_FAILED,
  error: true,
  payload: { ...error, userId },
});

export const DEACTIVATE_ADMINISTERED_USER =
  "administeredUsers/DEACTIVATE_ADMINISTERED_USER";
export const deactivateAdministeredUser = ({ userId }) => ({
  type: DEACTIVATE_ADMINISTERED_USER,
  payload: {
    userId,
  },
});

export const DEACTIVATE_ADMINISTERED_USER_SUCCEEDED =
  "administeredUsers/DEACTIVATE_ADMINISTERED_USER_SUCCEEDED";
export const deactivateAdministeredUserSucceeded = ({ user, userId }) => ({
  type: DEACTIVATE_ADMINISTERED_USER_SUCCEEDED,
  payload: {
    user,
    userId,
  },
});

export const DEACTIVATE_ADMINISTERED_USER_FAILED =
  "administeredUsers/DEACTIVATE_ADMINISTERED_USER_FAILED";
export const deactivateAdministeredUserFailed = ({ error, userId }) => ({
  type: DEACTIVATE_ADMINISTERED_USER_FAILED,
  error: true,
  payload: { ...error, userId },
});

export const REACTIVATE_ADMINISTERED_USER =
  "administeredUsers/REACTIVATE_ADMINISTERED_USER";
export const reactivateAdministeredUser = ({ userId }) => ({
  type: REACTIVATE_ADMINISTERED_USER,
  payload: {
    userId,
  },
});

export const REACTIVATE_ADMINISTERED_USER_SUCCEEDED =
  "administeredUsers/REACTIVATE_ADMINISTERED_USER_SUCCEEDED";
export const reactivateAdministeredUserSucceeded = ({ userId }) => ({
  type: REACTIVATE_ADMINISTERED_USER_SUCCEEDED,
  payload: {
    userId,
  },
});

export const REACTIVATE_ADMINISTERED_USER_FAILED =
  "administeredUsers/REACTIVATE_ADMINISTERED_USER_FAILED";
export const reactivateAdministeredUserFailed = ({ error, userId }) => ({
  type: REACTIVATE_ADMINISTERED_USER_FAILED,
  error: true,
  payload: { ...error, userId },
});

export const CHECK_ADMINISTERED_USER = "administeredUsers/CHECK_ADMINISTERED_USER";
export const checkAdministeredUser = ({ email, firstName, lastName }) => ({
  type: CHECK_ADMINISTERED_USER,
  payload: {
    email,
    firstName,
    lastName,
  },
});

export const CHECK_ADMINISTERED_USER_SUCCEEDED =
  "administeredUsers/CHECK_ADMINISTERED_USER_SUCCEEDED";
export const checkAdministeredUserSucceeded = () => ({
  type: CHECK_ADMINISTERED_USER_SUCCEEDED,
});

export const CHECK_ADMINISTERED_USER_FAILED =
  "administeredUsers/CHECK_ADMINISTERED_USER_FAILED";
export const checkAdministeredUserFailed = ({ error, email }) => ({
  type: CHECK_ADMINISTERED_USER_FAILED,
  error: true,
  payload: {
    ...error,
    email,
  },
});

export const CLEAR_MODIFY_USER_REQUEST_STATE =
  "administeredUsers/CLEAR_MODIFY_USER_REQUEST_STATE";
export const clearModifyUserRequestState = () => ({
  type: CLEAR_MODIFY_USER_REQUEST_STATE,
});

export const SET_PAGINATION_CURSOR = "administeredUsers/SET_PAGINATION_CURSOR";
export const setPaginationCursor = (cursor) => ({
  type: SET_PAGINATION_CURSOR,
  payload: {
    cursor,
  },
});

export const SAVE_PAGINATED_USERS = "administeredUsers/SAVE_PAGINATED_USERS";
export const savePaginatedUsers = (users) => ({
  type: SAVE_PAGINATED_USERS,
  payload: {
    users,
  },
});

export const SAVE_ADMINISTERED_USERS = "administeredUsers/SAVE_ADMINISTERED_USERS";
export const saveAdministeredUsers = () => ({
  type: SAVE_ADMINISTERED_USERS,
});

export const SET_ADMINISTERED_USERS = "administeredUsers/SET_ADMINISTERED_USERS";
export const setAdministeredUsers = (users) => ({
  type: SET_ADMINISTERED_USERS,
  payload: {
    users,
  },
});

export const CLEAR_SEARCH_FILTER = "administeredUsers/CLEAR_SEARCH_FILTER";
export const clearSearchFilter = () => ({
  type: CLEAR_SEARCH_FILTER,
});

export const PENDING = "pending";
export const SUCCESSFUL = "successful";
export const ERRORED = "errored";

const replaceUserInUsersArray = (users, updatedUser) =>
  S.pipe([
    S.map(
      S.filter(
        S.complement((user) =>
          S.equals(S.prop("id")(user))(S.prop("id")(updatedUser))
        )
      )
    ),
    S.append([updatedUser]),
  ])(users);

const userModificationStatus = (modificationType, status, error) => ({
  modificationType,
  status,
  error,
});

const SET_SEARCH_TERM = "SET_SEARCH_TERM";
export const setSearchTerm = (searchTerm) => ({
  type: SET_SEARCH_TERM,
  payload: searchTerm,
});

const SET_APPLIED_FILTERS = "SET_APPLIED_FILTERS";
export const setAppliedFilters = ({ filterName, filterOptions }) => ({
  type: SET_APPLIED_FILTERS,
  payload: { filterName, filterOptions },
});

const SET_SORT_STATE = "SET_SORT_STATE";
export const setSortState = (column, order) => ({
  type: SET_SORT_STATE,
  payload: { column, order },
});

const INITIAL_STATE = {
  requestAdministeredUsersPending: false,
  requestAdministeredUsersError: S.Nothing,
  checkAdministeredUserPending: false,
  checkAdministeredUserError: S.Nothing,
  createAdministeredUserPending: false,
  createAdministeredUserError: S.Nothing,
  userModificationStatuses: {},
  /*
  `usersCache` is used to revert users to its original state after clearing Searches and Filters.
  This comes with the cost of some duplication and having to maintain the cache in the reducer cases below.
  */
  users: S.Nothing,
  usersCache: S.Nothing,
  cursor: "",
  paginatedUsers: [],
  roleCombinations: {},
  // Sorting and Filters
  appliedFilters: S.Nothing,
  searchTerm: "",
  sortState: {
    // Sorting configuration
    column: "fullNameFormatted",
    order: "asc",
  },
};

const rolesWithAllAgencies = [
  CLIENT_ADMIN,
  OKTA_CLIENT_ADMIN,
  CB_ADMIN,
  CITYBASE_ADMIN,
];

export const hasAllAgencies = (safeRole, safePOSRole, agencies = []) =>
  rolesWithAllAgencies.some((role) =>
    [safeRole.value, safePOSRole.value].includes(role)
  ) || agencies.some((agency) => agency?.slug === SELECT_ALL_SLUG);

const applyFilteringAndSorting = (state, originalUsers = state.usersCache.value) => {
  let updatedUsers = [...originalUsers];

  // Apply text search
  if (state.searchTerm) {
    updatedUsers = applyTextSearchFilter(updatedUsers, state.searchTerm);
  }

  if (state.appliedFilters) {
    updatedUsers = applyMultipleFilters(updatedUsers, state.appliedFilters);
  }

  // Apply sorting
  if (state.sortState.column) {
    updatedUsers = applySorting(updatedUsers, state.sortState);
  }

  return S.Just(updatedUsers);
};

const applyTextSearchFilter = (users, searchTerm) => {
  if (!searchTerm) return users;

  const valuesToSearchFor = ["fullNameFormatted", "email"];

  return users.filter((user) =>
    valuesToSearchFor.some((key) =>
      user[key]?.value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
};

const applyMultipleFilters = (users, appliedFilters) => {
  if (!Object.keys(appliedFilters)?.length) return users;

  return users.filter((user) => {
    return matchesEveryAppliedFilter(user, appliedFilters);
  });
};

const matchesFilterOption = (val, filterOption) =>
  val?.toString().toLowerCase().includes(filterOption.name?.toLowerCase());

const matchesEveryAppliedFilter = (user, appliedFilters) => {
  return Object.keys(appliedFilters).every((filterName) => {
    const safeValue = getValueSafely(user, filterName);
    const filterOptions = appliedFilters[filterName];

    if (filterOptions?.length === 0) {
      return true;
    }
    if (!safeValue || safeValue.length === 0) {
      return false;
    }

    if (Array.isArray(safeValue)) {
      const arrayValues = getArrayValuesSafely(safeValue);
      return filterOptions.some((option) =>
        arrayValues.some((val) => matchesFilterOption(val, option))
      );
    } else {
      return filterOptions.some((option) => matchesFilterOption(safeValue, option));
    }
  });
};

export const ALL_AGENCIES = "All agencies";

export const toFormattedRole = (role) =>
  S.isJust(role)
    ? roleDictionary[role.value.value.toUpperCase().replace(/-/g, "_")] ||
      role.value.value
    : "";

export const getValueSafely = (user, column) => {
  const safeRole = user.role?.value || "";
  const safePOSRole = user.pos_role?.value || "";
  let columnValue = user[column]?.value;

  if (
    column === "agencies" &&
    hasAllAgencies(
      safeRole,
      safePOSRole,
      Array.isArray(columnValue) ? columnValue : []
    )
  ) {
    columnValue = ALL_AGENCIES;
  } else if (column === "role" || column === "pos_role") {
    columnValue = toFormattedRole(user[column]);
  }

  if (
    columnValue &&
    typeof columnValue === "object" &&
    !Array.isArray(columnValue)
  ) {
    return columnValue.value;
  }
  return columnValue ?? null;
};

const applySorting = (users, { column, order }) => {
  const collator = new Intl.Collator("en");
  return users.sort((userA, userB) => {
    const valueA = getValueSafely(userA, column);
    const valueB = getValueSafely(userB, column);

    if (order === "asc") {
      if (!valueA) return 1;
      if (!valueB) return -1;
      return collator.compare(valueA, valueB);
    } else {
      if (!valueA) return -1;
      if (!valueB) return 1;
      return collator.compare(valueB, valueA);
    }
  });
};

export const multipleSelectFiltersConfig = [
  {
    displayName: "Agencies",
    key: "agencies",
    icon: AgencyIcon,
    placeholder: "Find an agency",
  },
  {
    displayName: "Revenue Management Role",
    key: "role",
    icon: PersonIcon,
    placeholder: "Find a revenue management role",
  },
  {
    displayName: "Cashiering Role",
    key: "pos_role",
    icon: PersonIcon,
    placeholder: "Find a cashiering role",
  },
  {
    displayName: "Status",
    key: "statusLabel",
    icon: PaymentStatusIcon,
    placeholder: "Find a status",
  },
];

export const getArrayValuesSafely = (arr) => {
  return arr.map((val) => {
    if (val && typeof val === "object") {
      return val.name || val.value;
    } else {
      return val;
    }
  });
};

const getAllFilterOptions = (users) => {
  const allOptions = {};

  multipleSelectFiltersConfig.forEach((filter) => {
    const options = new Set();

    users?.forEach((user) => {
      const safeValue = getValueSafely(user, filter.key);

      if (safeValue) {
        if (Array.isArray(safeValue)) {
          const listValues = getArrayValuesSafely(safeValue);
          listValues.forEach((val) => options.add(val));
        } else {
          options.add(safeValue);
        }
      }
    });

    allOptions[filter.key] = Array.from(options);
  });

  return allOptions;
};

/**
 * We need to sort the "Name" column by full name because Okta returns first and last names separately.
 * An alternative would be adding secondary sorting the sortState, components, and applySorting function for this edge case.
 * */
const appendFullNameToUser = (user) => ({
  ...user,
  fullNameFormatted: {
    value: `${user.lastName.value}, ${user.firstName.value}`,
  },
});
const appendFullNameToUsers = (users) =>
  users.map((user) => appendFullNameToUser(user));

export const _reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_SEARCH_TERM:
      return {
        ...state,
        searchTerm: action.payload,
        users: applyFilteringAndSorting({
          ...state,
          searchTerm: action.payload,
        }),
      };
    case SET_APPLIED_FILTERS:
      // eslint-disable-next-line no-case-declarations
      const updatedFilters = {
        ...state.appliedFilters,
        [action.payload.filterName]: action.payload.filterOptions,
      };
      return {
        ...state,
        appliedFilters: updatedFilters,
        users: applyFilteringAndSorting({
          ...state,
          appliedFilters: updatedFilters,
        }),
      };
    case SET_SORT_STATE:
      return {
        ...state,
        sortState: action.payload,
        users: applyFilteringAndSorting({
          ...state,
          sortState: action.payload,
        }),
      };
    case FETCH_ROLE_COMBINATIONS_SUCCEEDED:
      return {
        ...state,
        roleCombinations: transformRoleCombinations(payload?.roleCombinations),
      };
    case REQUEST_ADMINISTERED_USERS:
      return {
        ...state,
        requestAdministeredUsersPending: true,
        requestPending: true,
      };
    case GET_ADMINISTERED_USERS_SUCCEEDED:
      return {
        ...state,
        requestPending: false,
        requestError: S.Nothing,
        users: applyFilteringAndSorting(state, appendFullNameToUsers(payload.users)),
        usersCache: applyFilteringAndSorting(
          state,
          appendFullNameToUsers(payload.users)
        ),
        paginatedUsers: [],
      };
    case GET_ADMINISTERED_USERS_FAILED:
      return {
        ...state,
        requestAdministeredUsersPending: false,
        requestPending: false,
        requestError: S.Just(payload),
      };
    case CHECK_ADMINISTERED_USER:
      return {
        ...state,
        checkAdministeredUserPending: true,
      };
    case CHECK_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        checkAdministeredUserPending: false,
      };
    case CHECK_ADMINISTERED_USER_FAILED:
      return {
        ...state,
        checkAdministeredUserPending: false,
        checkAdministeredUserError: S.Just(payload),
      };
    case CREATE_ADMINISTERED_USER:
      return {
        ...state,
        createAdministeredUserPending: true,
      };
    case CREATE_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        createAdministeredUserPending: false,
        users: S.append([payload.user])(state.users),
        usersCache: S.append([payload.user])(state.users),
      };
    case CREATE_ADMINISTERED_USER_FAILED:
      return {
        ...state,
        createAdministeredUserPending: false,
        createAdministeredUserError: S.Just(payload),
      };
    case CLEAR_CREATE_ADMINISTERED_USER_REQUEST_STATE:
      return {
        ...state,
        createAdministeredUserPending: false,
        createAdministeredUserError: S.Nothing,
      };
    case SUBMIT_ACTIVATE_ADMINISTERED_USER:
    case ACTIVATE_ADMINISTERED_USER:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            ACTIVATE_ADMINISTERED_USER,
            PENDING,
            S.Nothing
          ),
        },
      };
    case ACTIVATE_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        users: replaceUserInUsersArray(
          state.users,
          appendFullNameToUser(payload.user)
        ),
        usersCache: replaceUserInUsersArray(
          state.usersCache,
          appendFullNameToUser(payload.user)
        ),
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            ACTIVATE_ADMINISTERED_USER,
            SUCCESSFUL,
            S.Nothing
          ),
        },
      };
    case ACTIVATE_ADMINISTERED_USER_FAILED:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            ACTIVATE_ADMINISTERED_USER,
            ERRORED,
            payload
          ),
        },
      };
    case SUBMIT_DEACTIVATE_ADMINISTERED_USER:
    case DEACTIVATE_ADMINISTERED_USER:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            DEACTIVATE_ADMINISTERED_USER,
            PENDING,
            S.Nothing
          ),
        },
      };
    case DEACTIVATE_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        users: replaceUserInUsersArray(
          state.users,
          appendFullNameToUser(payload.user)
        ),
        usersCache: replaceUserInUsersArray(
          state.usersCache,
          appendFullNameToUser(payload.user)
        ),
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            DEACTIVATE_ADMINISTERED_USER,
            SUCCESSFUL,
            S.Nothing
          ),
        },
      };
    case DEACTIVATE_ADMINISTERED_USER_FAILED:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            DEACTIVATE_ADMINISTERED_USER,
            ERRORED,
            payload
          ),
        },
      };
    case SUBMIT_REACTIVATE_ADMINISTERED_USER:
    case REACTIVATE_ADMINISTERED_USER:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            REACTIVATE_ADMINISTERED_USER,
            PENDING,
            S.Nothing
          ),
        },
      };
    case REACTIVATE_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            REACTIVATE_ADMINISTERED_USER,
            SUCCESSFUL,
            S.Nothing
          ),
        },
      };
    case REACTIVATE_ADMINISTERED_USER_FAILED:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            REACTIVATE_ADMINISTERED_USER,
            ERRORED,
            payload
          ),
        },
      };
    case EDIT_ADMINISTERED_USER:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            EDIT_ADMINISTERED_USER,
            PENDING,
            S.Nothing
          ),
        },
      };
    case EDIT_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        users: replaceUserInUsersArray(
          state.users,
          appendFullNameToUser(payload.user)
        ),
        usersCache: replaceUserInUsersArray(
          state.usersCache,
          appendFullNameToUser(payload.user)
        ),
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            EDIT_ADMINISTERED_USER,
            SUCCESSFUL,
            S.Nothing
          ),
        },
      };
    case EDIT_ADMINISTERED_USER_FAILED:
      return {
        ...state,
        userModificationStatuses: {
          ...state.userModificationStatuses,
          [payload.userId]: userModificationStatus(
            EDIT_ADMINISTERED_USER,
            ERRORED,
            payload
          ),
        },
      };
    case CLEAR_MODIFY_USER_REQUEST_STATE:
      return {
        ...state,
        userModificationStatuses: {},
      };
    case SET_PAGINATION_CURSOR:
      return {
        ...state,
        cursor: payload.cursor,
      };
    case SAVE_PAGINATED_USERS:
      return {
        ...state,
        paginatedUsers: [...state.paginatedUsers, ...payload.users],
      };
    case SAVE_ADMINISTERED_USERS:
      return {
        ...state,
        requestAdministeredUsersPending: false,
      };
    case SET_ADMINISTERED_USERS:
      return {
        ...state,
        users: S.Just(payload.users),
      };
    case CLEAR_SEARCH_FILTER:
      return {
        ...state,
        searchTerm: "",
        users: applyFilteringAndSorting({
          ...state,
          searchTerm: "",
        }),
      };
    default:
      return state;
  }
};

// selectors
const getState = (state) => state.administeredUsers;
const getSubState = (key) => S.compose(S.prop(key))(getState);

export const getAdministeredUsers = getSubState("users");
export const getPaginationCursor = getSubState("cursor");
export const getPaginatedUsers = getSubState("paginatedUsers");
export const getCreateAdministeredUserPending = getSubState(
  "createAdministeredUserPending"
);
export const getCreateAdministeredUserError = getSubState(
  "createAdministeredUserError"
);
export const getCheckAdministeredUserPending = getSubState(
  "checkAdministeredUserPending"
);
export const getCheckAdministeredUserError = getSubState(
  "checkAdministeredUserError"
);
export const getUserModificationStatuses = getSubState("userModificationStatuses");
export const getRoleCombinations = getSubState("roleCombinations");

export const getAdministeredUserBeingEdited = createSelector(
  getAdministeredUsers,
  getUserBeingEdited,
  (users, userId) =>
    S.pipe([S.fromMaybe([]), S.find(S.compose(S.equals(userId))(S.prop("id")))])(
      users
    )
);

export const userWithStatusCanBeActivated = (userStatus) =>
  [OKTA_STATUSES.DEPROVISIONED, OKTA_STATUSES.STAGED].includes(userStatus);

export const userWithStatusCanBeReactivated = (userStatus) =>
  [OKTA_STATUSES.PROVISIONED].includes(userStatus);

export const _mapStateToProps = (state) => ({
  ...state.administeredUsers,
  administeredUsers: state.administeredUsers?.users?.value ?? [],
  sortState: state.administeredUsers.sortState,
  currentUserId: S.fromMaybe("")(getCurrentUserId(state)),
  modificationStatuses: getUserModificationStatuses(state),
  currentUserRole: getCurrentUserRole(state),
  currentUserAgencies: getCurrentUserAgencies(state),
  isPOSEnabled: getPointOfSaleEnabled(state),
  roleCombinations: getRoleCombinations(state),
  userPermissions: getCurrentUserPermissions(state),
});

export const _mapDispatchToProps = (dispatch) => ({
  openEditUserWorkflow: (userId, userRole) =>
    dispatch(openEditUserForm({ userId, userRole })),
  openPersonalInfoWorkflow: () => dispatch(openPersonalInfoForm()),
  savePaginatedUsers: (users) => dispatch(savePaginatedUsers(users)),
  requestAdministeredUsers: () => dispatch(requestAdministeredUsers()),
  saveAdministeredUsers: () => dispatch(saveAdministeredUsers()),
});

const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    forms: {},
    alertBars: {},
  },
  "administeredUsers"
);

const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
